import { keyed } from "@/lib/utils";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const SEMAFORO = {
  verde: "bg-emerald-950/60 border-emerald-500 text-emerald-400",
  amarillo: "bg-amber-950/60 border-amber-500 text-amber-400",
  rojo: "bg-red-950/60 border-red-600 text-red-400",
};

const Label = ({ children }) => (
  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">{children}</span>
);

const Field = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-800/70 last:border-0">
    <span className="text-zinc-500 text-sm">{label}</span>
    <span data-testid={testid} className={`text-sm text-right ${value == null ? "text-zinc-600 font-mono" : "text-zinc-100"}`}>
      {value == null || value === "" ? "null" : String(value)}
    </span>
  </div>
);

const Block = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-zinc-800 bg-[#0e0e10] p-3 space-y-1">
    <Label>{title}</Label>
    <div className="pt-1">{children}</div>
  </div>
);

const ConfidenceBadge = ({ value }) => {
  const v = typeof value === "number" ? value : null;
  const tone = v == null ? "bg-zinc-800 text-zinc-400" : v >= 0.75 ? "bg-emerald-500 text-black" : v >= 0.5 ? "bg-amber-500 text-black" : "bg-red-600 text-white";
  return <span data-testid="extraction-confidence" className={`font-mono text-xs px-2 py-1 rounded-md font-bold uppercase ${tone}`}>Confianza {v == null ? "—" : `${Math.round(v * 100)}%`}</span>;
};

const ContactsBlock = ({ contactos }) => (
  <Block title={`Contactos (${contactos.length})`} testid="extraction-contacts">
    {contactos.length === 0 && <p className="text-sm text-zinc-600 font-mono">ninguno</p>}
    {keyed(contactos, (x) => `${x.nombre}|${x.empresa || ""}`).map(([k, c], i) => (
      <div key={k} data-testid={`extraction-contact-${i}`} className="py-1.5 border-b border-zinc-800/70 last:border-0">
        <p className="text-zinc-100 font-semibold">{c.nombre}{c.apodo ? ` "${c.apodo}"` : ""}</p>
        <p className="text-xs text-zinc-400">{[c.empresa, c.rol_en_nota, ...(c.capacidades || [])].filter(Boolean).join(" · ")}</p>
      </div>
    ))}
  </Block>
);

export const ExtractionView = ({ data, model }) => {
  const opp = data.oportunidad || {};
  const etapa = data.cambio_etapa || {};
  const paso = data.proximo_paso || {};
  const com = data.acuerdo_comision || {};
  const inv = data.movimiento_inversion;
  const amb = data.ambiguedades || [];
  return (
    <section data-testid="extraction-view" className="fade-up rounded-xl border border-zinc-800 bg-[#121215] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-xl font-bold uppercase tracking-wide">Extracción estructurada</h2>
        <ConfidenceBadge value={data.confianza} />
      </div>
      <p data-testid="extraction-summary" className="text-zinc-300 text-sm leading-relaxed">{data.resumen}</p>
      <div className="flex flex-wrap gap-2">
        <span data-testid="extraction-tipo" className="font-mono text-xs px-2 py-1 rounded bg-zinc-800 text-cyan-400 uppercase">{data.tipo_nota}</span>
        <span className="font-mono text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-400">{model}</span>
      </div>
      {etapa.semaforo && (
        <div data-testid="extraction-semaforo" className={`flex items-center gap-3 p-3 rounded-lg border text-sm font-bold uppercase ${SEMAFORO[etapa.semaforo] || ""}`}>
          <span className="w-4 h-4 rounded-full bg-current" /> Semáforo {etapa.semaforo} {etapa.etapa ? `· ${etapa.etapa}` : ""}
        </div>
      )}
      <ContactsBlock contactos={data.contactos || []} />
      <Block title="Oportunidad" testid="extraction-opportunity">
        <Field label="Título" value={opp.titulo} testid="opp-title" />
        <Field label="Quien pide" value={opp.quien_pide} testid="opp-quien-pide" />
        <Field label="Quien resuelve" value={opp.quien_resuelve} testid="opp-quien-resuelve" />
        <Field label="Etapa" value={etapa.etapa} testid="opp-etapa" />
        <Field label="Monto potencial" value={opp.potencial?.monto} testid="opp-monto" />
      </Block>
      <Block title="Siguiente paso" testid="extraction-next-step">
        <Field label="Qué" value={paso.texto} testid="next-step-text" />
        <Field label="Cuándo (dicho)" value={paso.fecha_texto} testid="next-step-fecha-texto" />
        <Field label="Fecha" value={paso.fecha} testid="next-step-fecha" />
      </Block>
      <Block title="Comisión" testid="extraction-commission">
        <Field label="Modalidad" value={com.modalidad} testid="commission-modalidad" />
        <Field label="Porcentaje" value={com.porcentaje != null ? `${com.porcentaje}%` : null} testid="commission-porcentaje" />
        <Field label="Monto fijo" value={com.monto_fijo} testid="commission-monto" />
        <Field label="Condición" value={com.condicion} testid="commission-condicion" />
        <Field label="Estado" value={com.estado} testid="commission-estado" />
      </Block>
      <Block title="Inversión" testid="extraction-investment">
        {inv ? (<><Field label="Inversión" value={inv.inversion_nombre} /><Field label="Movimiento" value={inv.tipo_movimiento} /><Field label="Monto" value={inv.monto} /><Field label="Moneda" value={inv.moneda} /></>) : <p className="text-sm text-zinc-600 font-mono">null</p>}
      </Block>
      <Block title={`Necesidades (${(data.necesidades || []).length})`} testid="extraction-needs">
        {keyed(data.necesidades || [], (x) => x.texto).map(([k, n], i) => <p key={k} data-testid={`need-${i}`} className="text-sm text-zinc-200 py-1">• {n.texto}{n.quien_la_tiene ? ` — ${n.quien_la_tiene}` : ""}</p>)}
      </Block>
      <div data-testid="extraction-ambiguities" className={`p-3 rounded-lg text-xs font-mono space-y-1.5 border ${amb.length ? "bg-amber-950/40 border-amber-800/60 text-amber-200" : "bg-emerald-950/30 border-emerald-900 text-emerald-300"}`}>
        <div className="flex items-center gap-2 uppercase tracking-widest">
          {amb.length ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />} Ambigüedades ({amb.length})
        </div>
        {keyed(amb, (x) => `${x.campo}|${x.descripcion}`).map(([k, a], i) => <p key={k} data-testid={`ambiguity-${i}`}><b>{a.campo}</b>: {a.descripcion}{a.opciones?.length ? ` [${a.opciones.join(" / ")}]` : ""}</p>)}
      </div>
    </section>
  );
};
